// ─────────────────────────────────────────────────────────────
// Measurement Protocol — TAB 3: DUT CHAIN
//
// Moved verbatim out of the original monolithic MPCreationIndex.js.
// Same reordering behavior as Measurement Chain — rows carry a
// `sortOrder` derived from position and, once wired to a real API,
// should be sent the same way (SortOrder: idx + 1) on Save.
//
// VALIDATION: at least one instrument row (with something filled in)
// is always required. Reference No becomes mandatory on every such
// row ONLY when the app is running embedded inside Smile
// (isSmileIntegrated()) — the same convention used across every other
// MP tab — and stays optional otherwise. See `checkRows()` below.
// ─────────────────────────────────────────────────────────────
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch } from "react-redux";

import {
  toArray,
  Section,
  EmailPreviewModal,
  Toast,
  FormActions,
} from "../shared/mpShared";
import { InstrumentTable, dragReorderRows } from "../shared/InstrumentTable";
import {
  getCompanyID,
  ConfirmDialog,
  getEmployeeID,
  hasPrivilege,
  isSmileIntegrated,
  PRIVILEGES,
} from "../../../Common/UIComponents";
import {
  MP_DeleteMPDetails,
  MP_GetDUTChainDetails,
  MP_GetInstrumentList,
  MP_SaveDUTChain,
} from "../../../API_Service/MeasurementProtocol_Slice";

const DUTChainTab = forwardRef(function DUTChainTab(
  { mpID, mp, onSaved, onSubmitAll },
  ref,
) {
  const dispatch = useDispatch();
  const blank = () => ({
    _id: Date.now() + Math.random(),
    mpInstrumentDetailsId: 0,
    instrumentId: 0,
    refId: null,
    name: "",
    manufacturer: "",
    model: "",
    calDate: "",
  });
  const [rows, setRows] = useState([blank()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [instrumentsList, setInstrumentsList] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  // Row-level validation: { [row._id]: { refId: "message" } }
  const [rowErrors, setRowErrors] = useState({});
  // Table-level validation message, e.g. "at least one row is required"
  const [rowsError, setRowsError] = useState("");
  const tableRef = useRef(null);

  const notify = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 2500);
  };

  const addRow = () => {
    setRows((r) => [...r, blank()]);
    setRowsError("");
  };

  const removeRow = async () => {
    const { row } = deleteDialog;
    const id = row._id;
    if (!id) return;
    const isTempId = typeof id === "number" && id > 1000000000000;
    if (isTempId) {
      setRows((r) => r.filter((x) => x._id !== id));
      setDeleteDialog({ open: false, row: null });
      notify("Instrument Deleted Successfully");
      return;
    }
    try {
      const payload = {
        id: id,
        code: "DUTCHAIN",
        modifiedBy: getEmployeeID(),
      };
      const res = await dispatch(MP_DeleteMPDetails(payload));
      if (res?.payload?.data > 0) {
        setRows((r) => r.filter((x) => x._id !== id));
        setDeleteDialog({ open: false, row: null });
        notify("Instrument Deleted Successfully");
      } else {
        notify("Couldn't remove instrument. Please try again.", "error");
      }
    } catch (err) {
      console.error("Error deleting DUT chain instrument:", err);
      notify("An error occurred while removing the instrument.", "error");
    }
  };
  const selectedInstrumentIds = rows.map((r) => r.refId?.value).filter(Boolean);

  const onInstrumentChange = (row, selectedOption) => {
    const matchedInstrument = instrumentsList.find(
      (i) => Number(i.Instrument_ID) === Number(selectedOption?.value),
    );

    upd(row._id, "refId", selectedOption);

    if (matchedInstrument) {
      upd(row._id, "name", matchedInstrument.Instrument_Name || "");
      upd(row._id, "manufacturer", matchedInstrument.Make || "");
      upd(row._id, "model", matchedInstrument.Model_No || "");
      upd(row._id, "instrumentId", matchedInstrument.Instrument_ID || 0);
      upd(
        row._id,
        "calDate",
        matchedInstrument.Due_Date
          ? String(matchedInstrument.Due_Date).slice(0, 10)
          : "",
      );
    } else {
      upd(row._id, "name", "");
      upd(row._id, "manufacturer", "");
      upd(row._id, "model", "");
      upd(row._id, "instrumentId", 0);
      upd(row._id, "calDate", "");
    }
  };

  const upd = (id, k, v) => {
    setRows((r) => r?.map((x) => (x._id === id ? { ...x, [k]: v } : x)));
    setRowErrors((e) => {
      if (!e[id]) return e;
      return { ...e, [id]: { ...e[id], [k]: undefined } };
    });
    setRowsError("");
  };

  const handleReorder = (dragId, dropId) =>
    setRows((r) => dragReorderRows(r, dragId, dropId));

  const mapApiRowToState = (m) => ({
    _id: m.MP_Instrument_Details_Id || Date.now() + Math.random(),
    mpInstrumentDetailsId: m.MP_Instrument_Details_Id ?? 0,
    instrumentId: m.Instrument_Id ?? 0,
    refId: m.Instrument_No
      ? { value: m.Instrument_Id, label: m.Instrument_No }
      : null,
    name: m.Instrument_Name ?? "",
    manufacturer: m.Make ?? "",
    model: m.Model_No ?? "",
    calDate: m.Due_Date ? String(m.Due_Date).slice(0, 10) : "",
  });

  const GetDUTChainDetails = async () => {
    if (!mpID) return;
    setLoading(true);
    try {
      const payload = { companyID: getCompanyID(), mpID: mpID };
      const res = await dispatch(MP_GetDUTChainDetails(payload));
      const data = toArray(res?.payload?.data);

      setRows(data.map(mapApiRowToState));
    } catch (err) {
      console.error("Error fetching DUT chain details:", err);
      notify("Couldn't load the DUT chain. Please refresh.", "error");
    } finally {
      setLoading(false);
    }
  };

  const GetInstrumentList = async () => {
    try {
      const payload = { companyID: getCompanyID(), mpID: mpID };
      const res = await dispatch(MP_GetInstrumentList(payload));
      if (res?.payload?.data?.length) {
        setInstrumentsList(res.payload.data);
      }
    } catch (err) {
      console.error("Error fetching instrument list:", err);
      notify("Couldn't load the instrument list.", "error");
    }
  };

  useEffect(() => {
    GetDUTChainDetails();
    GetInstrumentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpID]);

  const getPreviewRows = () =>
    rows
      .filter((r) => r.name || r.manufacturer || r.model)
      .map((r, i) => ({
        label: `Instrument ${i + 1}`,
        value: [r.name, r.manufacturer, r.model].filter(Boolean).join(" · "),
      }));

  // At least one instrument row (with something actually filled in) is
  // always required. Reference Number becomes mandatory for every such
  // row ONLY when this app is running embedded inside Smile
  // (isSmileIntegrated()) — same convention used across the rest of
  // the MP tabs — and is left optional when running standalone.
  const checkRows = () => {
    const meaningfulRows = rows;
    if (meaningfulRows.length === 0 && isSmileIntegrated()) {
      return {
        rowErrs: {},
        rowsError: "At least one instrument row is required.",
      };
    }
    const rowErrs = {};

    meaningfulRows.forEach((r) => {
      if (!r.refId) rowErrs[r._id] = { refId: "Reference No is Required" };
    });
    return {
      rowErrs,
      rowsError: Object.keys(rowErrs).length
        ? "Reference No is required for every instrument row."
        : "",
    };
  };

  const handleSave = () =>
    new Promise((resolve, reject) => {
      const { rowErrs, rowsError: rErr } = checkRows();
      setRowErrors(rowErrs);
      setRowsError(rErr);
      if (rErr) {
        notify(rErr, "error");
        // reject(new Error(rErr));
        return;
      }
      const DutChainDetails = rows
        .filter((m) => m.name || m.manufacturer || m.model || m.refId)
        .map((m, idx) => ({
          MPInstrumentDetailsId: m.mpInstrumentDetailsId || 0,
          InstrumentNo: m?.refId?.label ?? "",
          InstrumentId: Number(m?.refId?.value ?? m.instrumentId ?? 0) || 0,
          InstrumentName: m?.name ?? "",
          Make: m?.manufacturer ?? "",
          ModelNo: m?.model ?? "",
          SortOrder: idx + 1,
        }));

      const payload = {
        MPID: mpID,
        DutChainDetails: DutChainDetails,
        SavedBy: getEmployeeID(),
        CompanyID: getCompanyID(),
      };

      dispatch(MP_SaveDUTChain(payload))
        .then((res) => {
          if (res?.payload?.data > 0) {
            setSaving(true);
            setTimeout(() => {
              setSaving(false);
              notify("DUT chain saved!");
              setTimeout(() => {
                onSaved?.();
                GetDUTChainDetails();
                resolve(res);
              }, 800);
            }, 800);
          } else {
            notify("Couldn't save the DUT chain.", "error");
            reject(new Error("Save returned no rows affected"));
          }
        })
        .catch((err) => {
          console.error("Error saving DUT chain:", err);
          notify("An error occurred while saving the DUT chain.", "error");
          reject(err);
        });
    });

  // Cross-tab Submit calls this the same way TestInfoTab/Communication
  // tab do: at least one instrument row is always required, and — when
  // the app is Smile-integrated — Reference No is required on every row.
  const validate = () => {
    const { rowErrs, rowsError: rErr } = checkRows();
    setRowErrors(rowErrs);
    setRowsError(rErr);
    return { valid: !rErr, field: rErr ? "rows" : null };
  };
  const focusField = () => {
    setTimeout(() => {
      tableRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
  };

  useImperativeHandle(ref, () => ({
    validate,
    focusField,
    getPreviewRows,
    save: handleSave,
  }));

  const handleDelete = useCallback((row) => {
    setDeleteBlocked(false);
    setDeleteDialog({ open: true, row });
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false, row: null });
    setDeleteBlocked(false);
  }, []);

  return (
    <>
      <Toast msg={toast.msg} show={toast.show} type={toast.type} />
      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mp={mp}
        subject={`DUT Chain Update — ${mp?.MPNo ?? "New MP"}`}
        rows={getPreviewRows()}
      />
      <div className="space-y-4 min-w-0">
        <Section
          icon="device_hub"
          title="DUT Chain"
          action={
            <div className="flex items-center gap-3">
              <span className="hidden sm:flex items-center gap-1.5 text-[10.5px] text-[#9eabac] font-medium">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-300" />
                Drag ⠿ to reorder
              </span>
              <button
                onClick={addRow}
                className="flex items-center gap-1.5 text-[#08757C] text-[12px] font-bold hover:underline"
              >
                <span className="material-symbols-outlined text-[15px]">
                  add
                </span>
                Add Instruments
              </button>
            </div>
          }
        >
          {rowsError && (
            <p className="text-[11px] font-semibold text-red-500 mb-2 flex items-center gap-1">
              <span className="material-symbols-outlined text-[13px]">
                error
              </span>
              {rowsError}
            </p>
          )}
          {loading ? (
            <div className="py-6 text-center text-sm text-[#9eabac]">
              Loading DUT chain...
            </div>
          ) : (
            <div ref={tableRef}>
              <InstrumentTable
                rows={rows}
                onRemove={handleDelete}
                onUpdate={upd}
                onReorder={handleReorder}
                emptyIcon="device_hub"
                emptyMsg="No DUT chain records"
                emptySub="Click Add Instruments to begin"
                instrumentsList={instrumentsList}
                onInstrumentChange={onInstrumentChange}
                selectedInstrumentIds={selectedInstrumentIds}
                requireRefId={isSmileIntegrated()}
                rowErrors={rowErrors}
              />
            </div>
          )}
        </Section>
        {hasPrivilege(PRIVILEGES.MP_EDIT) && (
          <FormActions
            onSave={handleSave}
            saving={saving}
            onPreview={() => setPreviewOpen(true)}
            onSubmit={onSubmitAll}
          />
        )}
      </div>
      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        title="Delete Instrument"
        message={`Are you sure you want to delete "${deleteDialog.row?.refId?.label || "Instrument"}"? This action cannot be undone.`}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={removeRow}
        onCancel={handleCancelDelete}
      />
    </>
  );
});

// ─────────────────────────────────────────────────────────────

export default DUTChainTab;
export { DUTChainTab };
